import { useContext, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Nav, NavDropdown, Badge } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { withThemeProvider, metadefenderService, logService } from '@mdc/services';
import UserContext from '../../user-context/UserContext';
import Spinner from '../../loading-spinner/loading/Loading';
import LanguageSelector from '../../main-menu/language-selector/LanguageSelector';
import { throttle } from 'lodash';

import './UserMenu.scss';

const UserMenu = ({
    onSignIn,
    onShowMenuClick,
    isMenuOpen,
    themeProvider,
    onLogoutClick,
    onLicensingClick
}) => {
    const { t, ready } = useTranslation();
    const userContext = useContext(UserContext);
    const [apikeyLimits, setApikeyLimits] = useState(undefined);
    const [isLimitLoading, setIsLimitLoading] = useState(false);
    const [leading, setLeading] = useState(true);
    const APIKEY_LIMITS = {
        'reputation_api': t('Reputation'),
        'prevention_api': t('Prevention'),
        'analysis_api': t('Analysis'),
    };

    const userOrganization = useMemo(() => {
        if (!userContext?.data?.organization) {
            return undefined;
        }

        const organizationLimits = Object.keys(userContext.data.organization)
            .filter((api) => api.includes('prevention') || api.includes('reputation') || api.includes('sandbox'))
            .reduce((newData, value) => {
                const processedValue = value.split('_')[1]?.includes('sandbox') ? 'analysis' : value.split('_')[1];
                newData[`${processedValue}_api`] = userContext.data.organization[value];
                return newData;
            }, {});

        return organizationLimits;
    }, [userContext]);

    const getLimitsStatus = async () => {
        try {
            const [apikeyLimitsStatusPromise] = await metadefenderService.apikey.getLimitsStatus();
            const apikeyLimitStatus = await apikeyLimitsStatusPromise;

            setApikeyLimits(apikeyLimitStatus.data);
            setIsLimitLoading(false);
        } catch (error) {
            setApikeyLimits(null);
            logService.error(error);
        }

        return;
    };

    const handleUserDropdownClick = throttle(async () => {
        setIsLimitLoading(true);
        await getLimitsStatus();

        if (leading) {
            setLeading(false);
        }
    }, 30000, { leading: leading, trailing: true });

    const getColorClassName = (name, remainingLimit) => {
        // eslint-disable-next-line camelcase
        const totalLimitPrevention = userOrganization ? userOrganization.prevention_api : userContext?.data?.limit_prevention;
        // eslint-disable-next-line camelcase
        const totalLimitReputation = userOrganization ? userOrganization.reputation_api : userContext?.data?.limit_reputation;
        // eslint-disable-next-line camelcase
        const totalLimitSandbox = userOrganization ? userOrganization.analysis_api : userContext?.data?.limit_sandbox;

        const getColor = (totalLimit, remainingLimit) => remainingLimit > Math.floor(totalLimit / 2) ? 'green' :
            remainingLimit <= Math.floor(totalLimit / 2) && remainingLimit > Math.floor(totalLimit / 5) ? 'yellow' :
                'red';

        switch (name) {
            case 'reputation_api':
                return getColor(totalLimitReputation, remainingLimit);

            case 'prevention_api':
                return getColor(totalLimitPrevention, remainingLimit);

            case 'analysis_api':
                return getColor(totalLimitSandbox, remainingLimit);

            default:
                return;
        }
    };

    const userLimitsDom = useMemo(() => {
        let liDom = null;
        if (apikeyLimits && !userOrganization) {
            liDom = Object.keys(APIKEY_LIMITS).map((api, index) => {
                const remainingLimit = apikeyLimits !== undefined && !isLimitLoading ? apikeyLimits[api] : <Spinner useTheme={themeProvider.getActive()} size="14px"/>;
                const limitName = APIKEY_LIMITS[api];
                const colorClassName = getColorClassName(api, remainingLimit);

                return <li key={index}>
                    <p>{limitName}</p>
                    <span className={`limit ${colorClassName}`}>{remainingLimit}</span>
                </li>;
            });
        }

        if (userOrganization) {
            liDom = Object.keys(userOrganization).map((api, index) => {
                const remainingLimit = userOrganization[api];
                const limitName = APIKEY_LIMITS[api];
                const colorClassName = getColorClassName(api, remainingLimit);

                return <li key={index}>
                    <p>{limitName}</p>
                    <span className={`limit ${colorClassName}`}>{remainingLimit}</span>
                </li>;
            });
        }

        if (liDom) {
            return <>
                <NavDropdown.Divider />
                <div className='dropdown-item p-0'>
                    <ul className='userLimits'>
                        {liDom}
                    </ul>
                </div>
            </>;
        }

        return;


    }, [apikeyLimits, isLimitLoading, userOrganization]);

    const onSignInBtnPress = (event) => {
        event.preventDefault();
        if (!onSignIn) {
            window.location.href = '/login';
            return;
        }

        onSignIn(event.target.href);
    };

    const onLogout = (event) => {
        event.preventDefault();

        onLogoutClick();
    };

    const languageSelector = (
        <LanguageSelector selectorClassNames='d-none d-sm-block language-selector' />
    );

    const storeSelector = (
        <Nav.Link onClick={onLicensingClick} className='storeSelector'>
            <span className='storeLink'>{t('Licensing')}</span>
        </Nav.Link>
    );

    const menuSelector = (
        <Nav.Link onClick={onShowMenuClick} className='menuSelector' aria-label='Menu selector'>
            <i className={classNames({ 'icon-menu': !isMenuOpen, 'icon-close': isMenuOpen })}/>
        </Nav.Link>
    );

    const loginButtonClasses = classNames({
        'loginButton': true,
        'd-block': true
    });

    const loginButton = useMemo(() => {
        if (userContext.state === userContext.STATES.PENDING) {
            return <Spinner useTheme={themeProvider.getActive()} size="14px"/>;
        }

        if (userContext.state === userContext.STATES.LOGGED_OUT) {
            return (<Nav.Link
                className={loginButtonClasses}
                href="/login"
                onClick={onSignInBtnPress}
            >
                {t('Sign In')}
            </Nav.Link>);
        }

        return null;
    }, [userContext.state, loginButtonClasses, t]);

    const reputationPoints = (userContext && userContext.data && userContext.data.reputation) ? userContext.data.reputation : 0;

    const userDropdown = (
        userContext.state === userContext.STATES.LOGGED_IN ?
            (<NavDropdown
                alignRight
                className='d-block header-nav-dropdown'
                id="header-nav-dropdown"
                title={<span className='userName'>{userContext.data.firstName}</span>}
                onClick={handleUserDropdownClick}
            >
                <NavDropdown.Item href="/account">
                    <span className="accountInfo">
                        {t('Account information')}
                    </span>
                    <span className='reputationPoints'>
                        {reputationPoints}
                        <span className='icon-award'></span>
                    </span>
                </NavDropdown.Item>
                <NavDropdown.Item href="/account/reports/dashboard">
                    <span>
                        {t('Dashboard')}
                    </span>
                    <Badge>{t('BETA')}</Badge>
                </NavDropdown.Item>
                <NavDropdown.Item href="/account/reports/statistics">
                    <span>
                        {t('Statistics')}
                    </span>
                    <Badge>{t('BETA')}</Badge>
                </NavDropdown.Item>
                {userContext.isPaidUser && <NavDropdown.Item href="/account/rsa-key">
                    {t('RSA Key')}
                </NavDropdown.Item>}
                {userContext.isOrganizationAdmin && <NavDropdown.Item href="/account/organization/security-rules">
                    {t('Security Rules')}
                </NavDropdown.Item>}
                <NavDropdown.Item href="/account/submission-history">
                    {t('Submission history')}
                </NavDropdown.Item>
                <NavDropdown.Item href="/account/vulnerability-submissions">
                    {t('Vulnerability submissions')}
                </NavDropdown.Item>
                <NavDropdown.Item href="/store">
                    {t('Upgrade')}
                </NavDropdown.Item>
                {userLimitsDom}
                <NavDropdown.Divider />
                <NavDropdown.Item
                    href="/#"
                    onClick={onLogout}
                >
                    {t('Logout')}
                </NavDropdown.Item>
            </NavDropdown>) :
            null
    );

    if (!ready) {
        return null;
    }

    return (
        <div className='user-menu-items'>
            <div className='align-items-center userMenuLinks'>
                {languageSelector}
                {loginButton}
                {userDropdown}
                {storeSelector}
            </div>
            {menuSelector}
        </div>
    );
};

UserMenu.propTypes = {
    onLogoutClick: PropTypes.func.isRequired,
    onShowMenuClick: PropTypes.func.isRequired,
    isMenuOpen: PropTypes.bool,
    onLicensingClick: PropTypes.func.isRequired,
    onSignIn: PropTypes.func,
    themeProvider: PropTypes.any
};

export default withThemeProvider(UserMenu);
